import { useEffect, useRef, useState } from "react";
import type { ButtonHTMLAttributes, ReactNode } from "react";
import { Icon } from "./Icon";

/**
 * M3 primitives.
 *
 * State layers are done the Material way — `.md-state` paints `currentColor`
 * over the container at the spec opacity, so hover/focus/press are the SAME
 * mechanism on every variant instead of a pile of hand-picked hover colors.
 */

// ─────────────────────────── Button ───────────────────────────

type ButtonVariant = "filled" | "tonal" | "outlined" | "text" | "elevated";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  filled: "bg-primary text-on-primary shadow-none",
  tonal: "bg-secondary-container text-on-secondary-container",
  outlined: "bg-transparent text-primary border border-outline",
  text: "bg-transparent text-primary",
  elevated: "bg-surface-low text-primary shadow-e1",
};

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  icon?: string;
  trailingIcon?: string;
  loading?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = "filled",
  icon,
  trailingIcon,
  loading = false,
  fullWidth = false,
  className = "",
  children,
  disabled,
  ...rest
}: ButtonProps) {
  const isDisabled = disabled || loading;
  return (
    <button
      {...rest}
      disabled={isDisabled}
      className={[
        "md-state md-label-large inline-flex items-center justify-center gap-2",
        "rounded-full px-6 min-h-[2.5rem] transition-shadow",
        BUTTON_VARIANTS[variant],
        fullWidth ? "w-full" : "",
        isDisabled
          ? "opacity-100 !bg-on-surface/[0.12] !text-on-surface/[0.38] !border-transparent !shadow-none"
          : "cursor-pointer",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {loading ? (
        <Spinner size={18} />
      ) : icon ? (
        <Icon name={icon} size={18} />
      ) : null}
      <span className="truncate">{children}</span>
      {trailingIcon && !loading ? <Icon name={trailingIcon} size={18} /> : null}
    </button>
  );
}

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  icon: string;
  /** Required — an icon alone is not a label. */
  label: string;
  filled?: boolean;
  size?: number;
}

export function IconButton({
  icon,
  label,
  filled = false,
  size = 22,
  className = "",
  ...rest
}: IconButtonProps) {
  return (
    <button
      {...rest}
      aria-label={label}
      title={label}
      className={[
        "md-state grid place-items-center rounded-full w-10 h-10 shrink-0",
        "text-on-surface-variant cursor-pointer",
        className,
      ].join(" ")}
    >
      <Icon name={icon} size={size} filled={filled} />
    </button>
  );
}

// ─────────────────────────── Surfaces ───────────────────────────

export interface CardProps {
  variant?: "elevated" | "filled" | "outlined";
  className?: string;
  children: ReactNode;
}

const CARD_VARIANTS = {
  elevated: "bg-surface-lowest shadow-e1",
  filled: "bg-surface-c",
  outlined: "bg-surface-lowest border border-outline-variant",
};

export function Card({ variant = "outlined", className = "", children }: CardProps) {
  return (
    <div className={`rounded-lg ${CARD_VARIANTS[variant]} ${className}`}>{children}</div>
  );
}

export function Divider({ className = "" }: { className?: string }) {
  return <hr className={`border-0 border-t border-outline-variant ${className}`} />;
}

// ─────────────────────────── Chips ───────────────────────────

export type ChipTone = "neutral" | "primary" | "success" | "warn" | "error" | "tertiary";

const CHIP_TONES: Record<ChipTone, string> = {
  neutral: "bg-surface-c text-on-surface-variant border-outline-variant",
  primary: "bg-primary-container text-on-primary-container border-transparent",
  success: "bg-success-container text-on-success-container border-transparent",
  warn: "bg-warn-container text-on-warn-container border-transparent",
  error: "bg-error-container text-on-error-container border-transparent",
  tertiary: "bg-tertiary-container text-on-tertiary-container border-transparent",
};

export function Chip({
  tone = "neutral",
  icon,
  filled,
  className = "",
  children,
}: {
  tone?: ChipTone;
  icon?: string;
  filled?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={[
        "md-label-medium inline-flex items-center gap-1 rounded-sm border px-2 py-1",
        CHIP_TONES[tone],
        className,
      ].join(" ")}
    >
      {icon ? <Icon name={icon} size={14} filled={filled} /> : null}
      {children}
    </span>
  );
}

// ─────────────────────────── Progress ───────────────────────────

export function Spinner({ size = 20, className = "" }: { size?: number; className?: string }) {
  return (
    <span
      role="progressbar"
      aria-label="جاري التحميل"
      className={`md-spin inline-block rounded-full border-2 border-current border-t-transparent ${className}`}
      style={{ width: size, height: size }}
    />
  );
}

export function LinearProgress({
  value,
  className = "",
  label,
}: {
  /** 0→1. Omit for indeterminate. */
  value?: number;
  className?: string;
  label?: string;
}) {
  const determinate = typeof value === "number";
  return (
    <div
      role="progressbar"
      aria-label={label ?? "جاري التنفيذ"}
      aria-valuenow={determinate ? Math.round(value * 100) : undefined}
      aria-valuemin={determinate ? 0 : undefined}
      aria-valuemax={determinate ? 100 : undefined}
      className={`relative h-1 w-full overflow-hidden rounded-full bg-primary-container ${className}`}
    >
      {determinate ? (
        <div
          className="absolute inset-y-0 start-0 rounded-full bg-primary transition-[width] duration-300 ease-out"
          style={{ width: `${Math.min(100, Math.max(0, value * 100))}%` }}
        />
      ) : (
        <div className="md-progress-bar absolute inset-y-0 rounded-full bg-primary" />
      )}
    </div>
  );
}

// ─────────────────────────── Banner / empty / error ───────────────────────────

export function Banner({
  tone = "primary",
  icon,
  title,
  children,
  action,
  className = "",
}: {
  tone?: ChipTone;
  icon?: string;
  title?: string;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "rounded-md border p-4 flex items-start gap-3",
        CHIP_TONES[tone],
        className,
      ].join(" ")}
    >
      {icon ? <Icon name={icon} size={20} className="mt-0.5 shrink-0" filled /> : null}
      <div className="min-w-0 flex-1">
        {title ? <p className="md-title-small mb-1">{title}</p> : null}
        {children ? <div className="md-body-medium">{children}</div> : null}
      </div>
      {action ? <div className="shrink-0">{action}</div> : null}
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  body,
  action,
}: {
  icon: string;
  title: string;
  body?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-full bg-surface-c text-on-surface-variant">
        <Icon name={icon} size={30} />
      </div>
      <h2 className="md-title-large text-on-surface">{title}</h2>
      {body ? <p className="md-body-medium max-w-sm text-on-surface-variant">{body}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({
  title = "صار خطأ",
  body,
  onRetry,
}: {
  title?: string;
  body: string;
  onRetry?: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-3 py-16 text-center">
      <div className="grid h-16 w-16 place-items-center rounded-full bg-error-container text-on-error-container">
        <Icon name="error" size={30} filled />
      </div>
      <h2 className="md-title-large text-on-surface">{title}</h2>
      <p className="md-body-medium max-w-sm text-on-surface-variant">{body}</p>
      {onRetry ? (
        <Button variant="tonal" icon="refresh" onClick={onRetry}>
          جرّب مرة ثانية
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({ className = "" }: { className?: string }) {
  return <div className={`md-pulse rounded-sm bg-surface-c ${className}`} aria-hidden="true" />;
}

// ─────────────────────────── Copy button ───────────────────────────

export function CopyButton({
  text,
  label = "نسخ",
  variant = "text",
}: {
  text: string;
  label?: string;
  variant?: ButtonVariant;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      // Clipboard API needs a secure context; fall back to a hidden textarea.
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.opacity = "0";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
      } catch {
        return;
      } finally {
        document.body.removeChild(ta);
      }
    }
    setCopied(true);
    if (timer.current !== null) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Button
      variant={variant}
      icon={copied ? "check" : "content_copy"}
      onClick={copy}
      className="!px-4"
    >
      {copied ? "اننسخ" : label}
    </Button>
  );
}

// ─────────────────────────── Dialog / bottom sheet ───────────────────────────

export function Dialog({
  open,
  onClose,
  title,
  children,
  labelledBy = "md-dialog-title",
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  labelledBy?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    // Focus the panel so the first Tab lands inside, not behind the scrim.
    panelRef.current?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center">
      <div
        className="md-scrim-in absolute inset-0 bg-scrim/[0.32]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        tabIndex={-1}
        className={[
          "md-sheet-in relative z-10 w-full max-w-md bg-surface-high shadow-e3 outline-none",
          "max-h-[92dvh] overflow-y-auto rounded-t-xl p-6 sm:rounded-xl",
        ].join(" ")}
      >
        <div className="mb-4 flex items-start justify-between gap-3">
          <h2 id={labelledBy} className="md-headline-small text-on-surface">
            {title}
          </h2>
          <IconButton icon="close" label="إغلاق" onClick={onClose} className="-me-2 -mt-1" />
        </div>
        {children}
      </div>
    </div>
  );
}
